import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePlayerStore, getPlayerActions } from "@/lib/player/player.store";
import { useBottomSheetGesture } from "./hooks/useBottomSheetGesture.hook";
import { useTranslation } from "react-i18next";

interface MobileChaptersOverlayProps {
	isOpen: boolean;
	onClose: () => void;
}

export function MobileChaptersOverlay({
	isOpen,
	onClose,
}: MobileChaptersOverlayProps) {
	const { t } = useTranslation();
	const chapters = usePlayerStore((state) => state.chapters);
	const activeChapterIndex = usePlayerStore(
		(state) => state.activeChapterIndex,
	);
	const formatTime = usePlayerStore((state) => state.formatTime);

	// Setup gesture for drag-to-close
	const { dragState, bind } = useBottomSheetGesture({
		onClose,
	});

	// If not open, return null
	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm animate-in fade-in duration-200">
			{/* Backdrop tap to close */}
			<div
				className="absolute inset-0"
				onClick={onClose}
				onTouchEnd={(e) => {
					e.stopPropagation();
					onClose();
				}}
			/>

			{/* Bottom sheet */}
			<div
				className="absolute bottom-0 left-0 right-0 bg-[#1c1c1e] rounded-t-3xl flex flex-col shadow-2xl animate-in slide-in-from-bottom duration-300"
				style={{
					maxHeight: "70vh",
					height: "70vh",
					paddingBottom: "env(safe-area-inset-bottom)",
					transform: dragState.isDragging
						? `translateY(${dragState.y}px)`
						: undefined,
					// Keep the sheet fully opaque during drag
					opacity: 1,
					transition: dragState.isDragging ? "none" : "transform 0.3s ease-out",
				}}
			>
				{/* Header with drag handle */}
				<div
					className="flex flex-col items-center p-4 border-b border-white/10 flex-shrink-0 touch-none"
					{...bind()}
				>
					{/* Drag handle indicator */}
					<div className="w-12 h-1.5 bg-white/20 rounded-full mb-3 cursor-grab active:cursor-grabbing" />
					<div className="w-full flex items-center justify-between">
						<h3 className="text-lg font-semibold text-white">
							{t("player.chapters")}
						</h3>
						<Button
							variant="ghost"
							size="icon"
							onClick={onClose}
							className="h-8 w-8 rounded-full hover:bg-white/10 text-gray-400 hover:text-white"
						>
							<X className="h-5 w-5" />
						</Button>
					</div>
				</div>

				{/* List - Native scrolling for better performance on mobile */}
				<div className="flex-1 overflow-y-auto overscroll-contain p-4">
					<div className="space-y-2 pb-4">
						{chapters.map((chapter, index) => {
							const isActive = index === activeChapterIndex;

							return (
								<button
									key={index}
									type="button"
									onClick={() => {
										getPlayerActions().goToChapter(index);
										onClose();
									}}
									className={`w-full text-left p-3 rounded-xl transition-none flex items-center gap-3 border ${
										isActive
											? "bg-orange-500/10 border-orange-500/30"
											: "bg-white/5 border-transparent active:bg-white/10"
									}`}
								>
									{/* Number */}
									<div
										className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
											isActive
												? "bg-orange-500 text-white"
												: "bg-white/10 text-gray-400"
										}`}
									>
										{index + 1}
									</div>

									{/* Info */}
									<div className="flex-1 min-w-0">
										<p
											className={`font-medium text-sm truncate ${
												isActive ? "text-orange-500" : "text-gray-200"
											}`}
										>
											{chapter.text}
										</p>
										<p className="text-xs text-gray-500 mt-0.5">
											{formatTime(chapter.startTime)}
										</p>
									</div>

									{/* Active indicator */}
									{isActive && (
										<div className="shrink-0 text-orange-500 px-2">
											<div className="h-2 w-2 rounded-full bg-orange-500" />
										</div>
									)}
								</button>
							);
						})}
					</div>
				</div>
			</div>
		</div>
	);
}
